import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Sessions } from "@app/shared/src/api/api.gen";
import { OmniUser, UserLogin, IUserLogin } from "@app/shared/src/models";
// Direct file import, not the helpers barrel: the v1 barrels contain circular re-exports that
// only resolve when an app enters shared through the root barrel (as the dashboard does).
import { sessionValue } from "@app/shared/src/helpers/sessionValue";

/**
 * Director session for Fleet Commander.
 *
 * Rides the same cookie session and generated clients as the main dashboard rather than inventing a
 * parallel auth path: Sessions.login sets the cookie, currentIdentity returns the user (including
 * the authToken the shared api_client attaches as X-AUTH-TOKEN on every write). The user is
 * persisted under the 'currentUser' sessionStorage key because that is where api_client looks —
 * this provider owns the lifecycle, but the storage location is the shared client's contract.
 */

type SessionState = {
    user?: OmniUser;
    /** True until the initial cookie check has resolved — routing decisions must wait for it. */
    isChecking: boolean;
    login: (email: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
    /** Re-pull the identity — after editing your own profile, so the header updates. */
    refresh: () => Promise<void>;
};

const SessionContext = createContext<SessionState | undefined>(undefined);

export function useSession(): SessionState {
    const value = useContext(SessionContext);
    if (!value) throw new Error("useSession must be used inside SessionProvider");
    return value;
}

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<OmniUser>();
    const [isChecking, setIsChecking] = useState(true);

    const persist = useCallback((next: OmniUser | undefined) => {
        const [, setStored] = sessionValue<OmniUser>("currentUser");
        setStored(next);
        setUser(next);
    }, []);

    // An existing cookie session survives a reload; ask once instead of bouncing a signed-in
    // director to the login page every time they refresh.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const identity = await Sessions.currentIdentity();
                if (!cancelled && identity?.id) persist(identity);
            } catch {
                // Not signed in — the login page is the correct destination, not an error.
            } finally {
                if (!cancelled) setIsChecking(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [persist]);

    /*
     * The shared api_client's 401 recovery contract.
     *
     * Writes carry X-AUTH-TOKEN, which the server mints into its *session* — and that session lives
     * in memory, so an API restart silently invalidates it while the auth cookie keeps working.
     * Reads keep succeeding and every write 401s. On a 401 the client dispatches this event, waits,
     * and retries once; re-fetching the identity re-mints the token, so the retry lands. Without
     * this listener the console just showed "unauthorized" to a perfectly signed-in director.
     */
    useEffect(() => {
        const onCheckAuthState = () => {
            Sessions.currentIdentity()
                .then((identity) => {
                    if (identity?.id) persist(identity);
                })
                .catch(() => {
                    // Genuinely signed out — drop the user so the guard routes to login.
                    persist(undefined);
                });
        };
        document.addEventListener("auth:checkAuthState", onCheckAuthState);
        return () => document.removeEventListener("auth:checkAuthState", onCheckAuthState);
    }, [persist]);

    const login = useCallback(
        async (email: string, password: string) => {
            await Sessions.login(new UserLogin({ email, password, rememberMe: true } as IUserLogin));
            // Login only sets the cookie; the identity (and the authToken every later write needs)
            // comes from a second call by design.
            const identity = await Sessions.currentIdentity();
            if (!identity?.id) throw new Error("Signed in, but no identity came back");
            persist(identity);
        },
        [persist],
    );

    const logout = useCallback(async () => {
        try {
            await Sessions.logout();
        } finally {
            // Locally signed out even if the server call failed — a dead session cookie shouldn't
            // trap someone on a console they can't use.
            persist(undefined);
        }
    }, [persist]);

    const refresh = useCallback(async () => {
        const identity = await Sessions.currentIdentity();
        if (identity?.id) persist(identity);
    }, [persist]);

    const value = useMemo(
        () => ({ user, isChecking, login, logout, refresh }),
        [user, isChecking, login, logout, refresh],
    );

    return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};
